import i18next from "@app/i18n"
import { Unit } from "@shared/api/types"

const DEFAULT_LOCALE = "de-DE"
const DEFAULT_FRACTION_DIGITS = 0
const TONS_FRACTION_DIGITS = 3

export enum UnitType {
	Kg = "kg",
	KgCO2 = "kgCO2",
	Ton = "ton",
}

/**
 * Formats a number according to the unit type's precision rules.
 * Uses locale-aware formatting based on the current i18n language.
 */
export const formatNumber = (
	value: number | string,
	unit: Unit,
	config?: Intl.NumberFormatOptions,
): string => {
	const fractionDigits =
		unit === Unit.Tons ? TONS_FRACTION_DIGITS : DEFAULT_FRACTION_DIGITS
	return formatNumberByFraction(value, fractionDigits, config)
}

/**
 * Formats a number with explicit fraction digit control.
 * Returns "0" for zero or invalid values.
 */
export const formatNumberByFraction = (
	value: number | string,
	fractionDigits: number,
	config?: Intl.NumberFormatOptions,
): string => {
	const numValue =
		typeof value === "string" ? Number.parseFloat(value) : value

	if (Number.isNaN(numValue) || numValue === 0) {
		return "0"
	}

	const locale = i18next.resolvedLanguage ?? DEFAULT_LOCALE
	const formatConfig = config ?? {
		minimumFractionDigits: 0,
		maximumFractionDigits: fractionDigits,
	}

	return numValue.toLocaleString(locale, formatConfig)
}

/**
 * Formats a number with its localized unit suffix.
 * @example formatNumberWithUnit(1500.5, UnitType.Ton) => "1.500,5 t" (de-DE)
 */
export const formatNumberWithUnit = (
	value: number | string,
	unitType: UnitType = UnitType.Kg,
): string => {
	const localUnit = i18next.t(`shared:units.${unitType}`)
	const formattedNumber = formatNumber(
		value,
		unitType === UnitType.Ton ? Unit.Tons : Unit.KgCo2,
	)

	return `${formattedNumber} ${localUnit}`
}
